import React from "react";
import { Box, Typography } from "@mui/material";
import { W } from "./wizardTokens";
import { LETTERS, parseList } from "./reviewModel";

// The backend runs 3 real, separately-prompted Gemini calls per file (see GeminiImportPipeline) -
// STRUCTURE, then SCORING, then REVIEW - and the row's `phase` advances as each one actually
// completes. These labels are polled real progress, not a decorative timer.
const PHASES_BY_TYPE = {
  "skill-sheet": ["Finding skill steps", "Assigning points and critical flags", "Checking for anything unclear"],
  test: ["Finding questions", "Matching the answer key", "Checking for anything unclear"],
};
const PHASE_INDEX = { STRUCTURE: 0, SCORING: 1, REVIEW: 2 };

function extOf(name) {
  const ext = (name.split(".").pop() || "").toUpperCase().slice(0, 4);
  return ext === "DOCX" ? "DOC" : ext === "PPTX" ? "PPT" : ext === "XLSX" ? "XLS" : ext;
}

const STATUS_LABEL = { waiting: "Waiting", uploading: "Scanning…", ok: "Drafted", duplicate: "Possible duplicate", failed: "Failed" };

function QueueItem({ item, active, onRetry }) {
  const st = item.status;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr)", gap: "10px", alignItems: "center", p: "10px", borderRadius: "10px", bgcolor: active ? W.aiBg : "transparent", border: `1px solid ${active ? W.aiLine : "transparent"}` }}>
      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: W.surface2, display: "grid", placeItems: "center", fontSize: "9.5px", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{extOf(item.file.name)}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "13.5px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.file.name}</Typography>
        <Typography sx={{ fontSize: "12px", color: st === "failed" ? W.danger : st === "duplicate" ? W.warn : st === "ok" ? W.success : W.ink3 }}>
          {item.error || (st === "ok" && item.sheetCount > 1 ? `${item.sheetCount} skill sheets found` : STATUS_LABEL[st])}
        </Typography>
        {st === "failed" && onRetry && (
          <Box component="button" type="button" onClick={onRetry}
            sx={{
              font: "inherit", fontSize: "12px", fontWeight: 600, color: W.primary, bgcolor: W.surface,
              border: `1px solid ${W.primary}`, borderRadius: "6px", px: "10px", py: "4px", mt: "6px", cursor: "pointer",
              "&:hover": { bgcolor: W.aiBg },
            }}>
            Try again
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** Decorative "reading a document" visual — a looping scan beam, no claim about real progress. */
function PaperVisual() {
  return (
    <Box sx={{
      bgcolor: W.ink, borderRadius: "10px", p: "20px", display: "grid", placeItems: "center", position: "relative", overflow: "hidden", minHeight: 160,
    }}>
      <Box sx={{ position: "relative", width: 96, aspectRatio: "8.5/11", bgcolor: "#fff", borderRadius: "3px", p: "10px 8px", display: "flex", flexDirection: "column", gap: "5px", overflow: "hidden" }}>
        <Box sx={{ height: 7, width: "65%", borderRadius: "2px", bgcolor: W.line, mb: "3px" }} />
        {[88, 76, 84, 60, 90, 70, 80, 55].map((w, i) => (
          <Box key={i} sx={{ height: 4, width: `${w}%`, borderRadius: "2px", bgcolor: W.line }} />
        ))}
        <Box sx={{
          position: "absolute", left: 0, right: 0, height: 22, top: -22,
          background: `linear-gradient(to bottom, transparent, ${W.ai}55)`,
          borderBottom: `2px solid ${W.ai}`,
          animation: "kbScanBeam 1.8s ease-in-out infinite",
          "@keyframes kbScanBeam": { "0%": { top: -22 }, "100%": { top: "100%" } },
        }} />
      </Box>
    </Box>
  );
}

function PhaseList({ phases, activeIndex }) {
  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
      {phases.map((p, i) => {
        const done = i < activeIndex;
        const active = i === activeIndex;
        return (
          <Box key={p} component="li" sx={{ display: "grid", gridTemplateColumns: "18px minmax(0,1fr)", gap: "10px", alignItems: "center", fontSize: "13.5px", color: active || done ? W.ink : W.ink3, fontWeight: active ? 700 : 400 }}>
            {done ? (
              <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: W.success, color: "#fff", display: "grid", placeItems: "center" }}>
                <Box component="svg" width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></Box>
              </Box>
            ) : (
              <Box sx={{
                width: 18, height: 18, borderRadius: "50%", border: `1.5px solid ${active ? W.ai : W.line}`,
                borderTopColor: active ? "transparent" : undefined,
                animation: active ? "kbSpin .8s linear infinite" : "none",
                "@keyframes kbSpin": { to: { transform: "rotate(360deg)" } },
              }} />
            )}
            {p}
          </Box>
        );
      })}
    </Box>
  );
}

function answerLetters(q) {
  if (q.type === "TRUE_FALSE") return typeof q.tfCorrect === "boolean" ? (q.tfCorrect ? "T" : "F") : null;
  const letters = (q.options || []).map((o, i) => (o.isCorrect ? LETTERS[i] : null)).filter(Boolean);
  return letters.length ? letters.join(",") : null;
}

/**
 * What the AI has actually extracted so far - filled in from the row's partial payload once the
 * first pass lands, then gains answers/points once the second pass lands. Before the first pass
 * there's nothing real to show yet, so it's placeholder skeleton rows only.
 */
function FoundList({ item, isTest }) {
  const list = item.partial ? parseList(item.partial, isTest ? "TEST" : "SKILL_SHEET") : [];
  const label = isTest ? "Questions found" : "Steps found";
  return (
    <Box sx={{ borderTop: `1px solid ${W.line}`, pt: "12px", minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "10.5px", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: W.ink3, mb: "8px" }}>
        <span>{label}</span>
        {list.length > 0 && <span>{list.length}</span>}
      </Box>
      {list.length === 0 ? (
        [0, 1, 2].map((i) => (
          <Box key={i} sx={{ display: "grid", gridTemplateColumns: "24px minmax(0,1fr) 36px", gap: "8px", alignItems: "center", py: "8px" }}>
            <Box sx={{ height: 10, borderRadius: "4px", bgcolor: W.surface2 }} />
            <Box sx={{ height: 10, width: `${80 - i * 15}%`, borderRadius: "4px", bgcolor: W.surface2, animation: "kbPulse 1.4s ease-in-out infinite", "@keyframes kbPulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: .45 } } }} />
            <Box sx={{ height: 10, borderRadius: "4px", bgcolor: W.surface2 }} />
          </Box>
        ))
      ) : (
        <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
          {list.map((q, i) => {
            const answer = isTest ? answerLetters(q) : (q.pointValue != null ? `${q.pointValue} pts` : null);
            return (
              <Box key={q.id || i} sx={{
                display: "grid", gridTemplateColumns: "24px minmax(0,1fr) auto", gap: "8px", alignItems: "center", py: "7px", fontSize: "13px",
                opacity: 0, animation: "kbFoundIn .3s ease forwards", animationDelay: `${Math.min(i, 12) * 60}ms`,
                "@keyframes kbFoundIn": { from: { opacity: 0, transform: "translateY(3px)" }, to: { opacity: 1, transform: "none" } },
              }}>
                <Box sx={{ fontFamily: "var(--font-mono)", fontSize: "11.5px", color: W.ink3 }}>{String(i + 1).padStart(2, "0")}</Box>
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{isTest ? q.questionText : q.criteria}</Box>
                <Box sx={{ fontSize: "11.5px", fontWeight: 700, color: answer ? W.success : W.ink4, whiteSpace: "nowrap" }}>{answer ? (isTest ? `✓ ${answer}` : answer) : "–"}</Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default function ScanStep({ queue, aiType, onRetry }) {
  const activeIndex = queue.findIndex((q) => q.status === "uploading");
  const current = activeIndex >= 0 ? queue[activeIndex] : null;
  const doneCount = queue.filter((q) => q.status !== "waiting" && q.status !== "uploading").length;
  const allDone = doneCount === queue.length;
  const failures = queue.filter((q) => q.status === "failed").length;
  // a multi-sheet file yields one draft per sheet, so count sheets rather than files
  const draftCount = queue.filter((q) => q.status !== "failed").reduce((n, q) => n + (q.sheetCount || 1), 0);

  const phases = PHASES_BY_TYPE[aiType] || PHASES_BY_TYPE["skill-sheet"];
  const phase = current ? (PHASE_INDEX[current.phase] ?? 0) : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {allDone ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "32px minmax(0,1fr)", gap: "12px", alignItems: "center", p: "14px 16px", borderRadius: "10px", bgcolor: failures ? W.warnBg : W.successBg }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: failures ? W.warn : W.success, color: "#fff", display: "grid", placeItems: "center", fontWeight: 700 }}>{failures ? "!" : "✓"}</Box>
          <Typography sx={{ fontSize: "14px" }}>
            <b>Scan complete.</b> {draftCount} draft{draftCount === 1 ? "" : "s"} ready for review
            {failures ? `, ${failures} file${failures > 1 ? "s" : ""} failed` : ""}.
          </Typography>
        </Box>
      ) : queue.length > 1 ? (
        <Typography sx={{ fontSize: "13.5px", color: W.ink3 }}>
          Scanning file {Math.min(doneCount + 1, queue.length)} of {queue.length}.
        </Typography>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "220px minmax(0,1fr)" }, gap: "16px", alignItems: "start" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {queue.map((q, i) => (
            <QueueItem key={`${q.file.name}-${i}`} item={q} active={i === activeIndex} onRetry={onRetry ? () => onRetry(i) : null} />
          ))}
        </Box>

        <Box sx={{ border: `1px solid ${W.line}`, borderRadius: "12px", overflow: "hidden", display: current ? "grid" : "flex", gridTemplateColumns: current ? "160px minmax(0,1fr)" : undefined, alignItems: current ? "stretch" : "center", justifyContent: current ? undefined : "center", minHeight: 160 }}>
          {current ? (
            <>
              <PaperVisual />
              <Box sx={{ p: "18px 20px", display: "flex", flexDirection: "column", gap: "14px", minWidth: 0 }}>
                <PhaseList phases={phases} activeIndex={phase} />
                <FoundList item={current} isTest={aiType === "test"} />
              </Box>
            </>
          ) : (
            <Typography sx={{ fontSize: "13.5px", color: W.ink3, p: "16px" }}>{allDone ? "All files scanned." : "Waiting to start…"}</Typography>
          )}
        </Box>
      </Box>

      {allDone && (
        <Box sx={{ p: "12px 14px", borderRadius: "10px", bgcolor: W.aiBg, border: `1px solid ${W.line}`, fontSize: "13.5px", lineHeight: 1.45 }}>
          AI drafts can misread handwriting, points, and step order. Compare each draft against the original before saving.
        </Box>
      )}
    </Box>
  );
}
